import { useEffect, useMemo, useState } from 'react';
import { Alert, Image, Pressable, View, InteractionManager, Keyboard } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import * as ImagePicker from 'expo-image-picker';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { db } from '@/lib/firebase';
import { OnboardingProgress } from '@/components/onboarding/OnboardingProgress';
import { getOnboardingStep, getTotalOnboardingSteps } from '@/constants/onboarding';
import { PRESET_AVATAR_IDS, getAvatarImage } from '@/constants/avatars';
import { LoadingState } from '@/components/ui/LoadingState';
import { withTimeout } from '@/utils/withTimeout';
import { useOnboardingStepAnalytics } from '@/hooks/useOnboardingStepAnalytics';

const AVATAR_SAVE_TIMEOUT_MS = 8000;

export default function AvatarSelectionScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const theme = useTheme();
  const profile = useUserStore((state) => state.profile);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const setAvatar = useUserStore((state) => state.setAvatar);
  const isNewUser = useUserStore((state) => state.isNewUser);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const authReady = useUserStore((state) => state.authReady);
  const [saving, setSaving] = useState(false);

  const rawMode = Array.isArray(params?.mode) ? params.mode[0] : params?.mode;
  const rawReturnTo = Array.isArray(params?.returnTo) ? params.returnTo[0] : params?.returnTo;
  const isEditMode = rawMode === 'edit';
  const { logCompleted } = useOnboardingStepAnalytics('avatar', !isEditMode);
  const returnTo = useMemo(() => {
    if (rawReturnTo && typeof rawReturnTo === 'string') {
      try {
        return decodeURIComponent(rawReturnTo);
      } catch {
        return rawReturnTo;
      }
    }
    return '/(tabs)/profile';
  }, [rawReturnTo]);

  const selectedPreset = profile.avatar?.type === 'preset' ? profile.avatar.id : undefined;

  const proceed = async () => {
    Keyboard.dismiss();
    await new Promise<void>((resolve) => {
      InteractionManager.runAfterInteractions(() => resolve());
    });

    if (isEditMode) {
      router.replace(returnTo);
    } else {
      logCompleted();
      router.replace('/(onboarding)/travel-type');
    }
  };

  const persistAvatar = async (avatar: { type: 'preset'; id: string } | { type: 'upload'; uri: string }) => {
    if (!firebaseUser?.uid) {
      setAvatar(avatar);
      void proceed();
      return;
    }

    setSaving(true);

    try {
      await withTimeout(
        setDoc(
          doc(db, 'users', firebaseUser.uid),
          {
            avatar,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
        AVATAR_SAVE_TIMEOUT_MS,
        'avatar save',
      );
      setAvatar(avatar);
      await proceed();
    } catch (error) {
      console.error('Failed to save avatar', error);
      Alert.alert(
        'Saving offline',
        'Could not save your avatar to the server right now, but we saved it locally so you can continue.',
      );
      setAvatar(avatar);
      await proceed();
    } finally {
      setSaving(false);
    }
  };

  const handlePresetSelect = (id: string) => {
    if (saving) {
      return;
    }
    persistAvatar({ type: 'preset', id });
  };

  const handleUpload = async () => {
    if (saving) {
      return;
    }

    try {
      // Request permission to access media library
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      
      if (permissionResult.status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please allow access to your photo library to upload a profile photo.',
          [{ text: 'OK' }]
        );
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const selectedImage = result.assets[0];
        // Save the image URI
        await persistAvatar({ type: 'upload', uri: selectedImage.uri });
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to select image. Please try again.');
    }
  };

  useEffect(() => {
    if (!isEditMode && authReady && !isNewUser && hasCompletedOnboarding) {
      router.replace('/(tabs)');
    }
  }, [authReady, hasCompletedOnboarding, isEditMode, isNewUser, router]);

  if (!isEditMode && authReady && !isNewUser && hasCompletedOnboarding) {
    return <LoadingState variant="fullscreen" message="Opening Tripvio…" />;
  }

  const currentStep = getOnboardingStep('avatar') ?? 3;
  const totalSteps = getTotalOnboardingSteps();
  const showProgress = !isEditMode;
  const screenTitle = isEditMode ? 'Update your avatar' : 'Choose your travel vibe';
  const screenSubtitle = isEditMode
    ? 'Upload a photo or pick a preset to refresh your profile.'
    : 'Pick an avatar or upload a photo so your Tripvio account feels like you.';

  return (
    <Screen
      title={screenTitle}
      subtitle={screenSubtitle}
      scrollable
      showBackButton={true}
      onBack={() => {
        if (isEditMode) {
          router.replace(returnTo);
        } else {
          router.replace('/(onboarding)/profile-details');
        }
      }}
    >
      {showProgress && <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />}
      <View style={{ gap: theme.spacing(2) }}>
        <Button label="Upload a photo" onPress={handleUpload} disabled={saving} />

        <Caption color={theme.colors.textSecondary}>Or choose a preset avatar</Caption>

        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: theme.spacing(1.5),
          }}
        >
          {PRESET_AVATAR_IDS.map((avatarId) => {
            const isSelected = selectedPreset === avatarId;
            const avatarImage = getAvatarImage(avatarId);
            if (!avatarImage) {
              return null;
            }

            return (
              <Pressable
                key={avatarId}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                onPress={() => handlePresetSelect(avatarId)}
                style={({ pressed }) => ({
                  width: '30%',
                  aspectRatio: 1,
                  minWidth: 96,
                  borderWidth: 2,
                  borderRadius: theme.radii.lg,
                  borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                  backgroundColor: theme.colors.surface,
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing(1.25),
                  opacity: pressed ? 0.85 : 1,
                })}
              >
                <Image
                  source={avatarImage}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 32,
                  }}
                  resizeMode="contain"
                />
              </Pressable>
            );
          })}
        </View>

        <Body color={theme.colors.textSecondary}>
          You can change your avatar later from Settings &gt; Profile.
        </Body>
        <Button
          label={isEditMode ? 'Back to profile' : 'Skip for now'}
          variant="ghost"
          onPress={proceed}
          disabled={saving}
        />
      </View>
    </Screen>
  );
}


